import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AbstractComponent } from '@shared/common/abstract.component';
import { InputDialogComponent } from '@shared/dialogs/input/input-dialog.component';
import type {
    NonProRataRate,
    NonProRataRateDictionary
} from '@shared/modules/rates/non-pro-rata-rate';
import {
    createNonProRataRateDictionary
} from '@shared/modules/rates/non-pro-rata-rate';
import { clone, roundToNearest } from '@shared/utils';
import { equals, isPresent } from '@shared/utils/helpers';
import { sortBy } from 'lodash-es';
import { AppSnackBar } from '../../../services/snackbar/app-snackbar.service';

@Component({
    selector: 'app-non-pro-rata-rates',
    styleUrls: ['./non-pro-rata-rates.component.scss'],
    template: `
        <p class="hint">
            The calculation will attempt to find a matching non pro-rata rate first, and then calculate the
            remainder using the base rate. Only one non pro-rata rate can be used in a calculation.

            When using non pro-rata rates, you should also add a rate for 1h.
        </p>

        <div class="ranges-table-container">
            <table class="ranges-table">
                <tbody>

                <tr>
                    <th *ngFor="let rate of ratesDurations; trackBy: _trackRateFn" class="rate" data-testid="info:rate-duration">
                        {{ rate | appDuration }}

                        <button cll-icon-button
                                cllTooltip="Delete rate"
                                (click)="deleteRate(rate)">
                            <mat-icon>clear</mat-icon>
                        </button>
                    </th>

                    <th class="add-rate-heading" rowspan="2">
                        <button cll-tonal-button
                                colour="variant"
                                data-testid="button:add-non-pro-rata-rate"
                                cllTooltip="Add non-pro-rata rate"
                                (click)="addRate()">
                            <cll-icon>add</cll-icon>
                            Add rate
                        </button>
                    </th>
                </tr>

                <tr>
                    <td *ngFor="let rate of ratesDurations; trackBy: _trackRateFn" class="rate">
                        <app-input-currency placeholder="Amount"
                                            floatLabel="never"
                                            data-testid="input:non-pro-rata-rate-amount"
                                            appearance="standard"
                                            [(ngModel)]="_model[rate].amount"
                                            (ngModelChange)="onModelChanged()" />
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
    `,
})
export class NonProRataRatesComponent extends AbstractComponent {
    @Input() defaultAmount: number;

    ratesDurations: number[] = [];

    _rates: NonProRataRate[];
    _model: NonProRataRateDictionary;

    @Input()
    set rates(value: NonProRataRate[]) {
        this._rates = value;
        this._model = createNonProRataRateDictionary(clone(value));

        this._updateRateDurations();
    }

    @Output() ratesChange = new EventEmitter<NonProRataRate[]>();

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private dialog: MatDialog,
        private snackBar: AppSnackBar
    ) {
        super();
    }

    onModelChanged() {
        const previousRates = clone(this._rates);

        const updatedRates = sortBy(
            Object.values(this._model).filter(rate => rate.duration && isPresent(rate.amount)),
            'duration'
        );

        const hasChanged = !equals(previousRates, updatedRates);

        if (hasChanged) {
            this.ratesChange.emit(updatedRates);
        }
    }

    addRate() {
        const lastDuration = this.ratesDurations[this.ratesDurations.length - 1];

        InputDialogComponent.show(this.dialog, {
            title: 'Add non-pro-rata rate',
            label: 'Rate duration',
            type: 'duration',
            okay: 'Add rate',
            initialValue: roundToNearest(
                (lastDuration ?? 0) + 900,
                900
            )
        }).afterClosed()
            .pipe(this.takeUntilDestroyed())
            .subscribe(result => {
                if (result?.success) {
                    const duration = result.value;

                    if (duration && !isNaN(duration) && duration !== 0 && !this._model.hasOwnProperty(duration)) {
                        this._model[duration] = {
                            duration,
                            amount: this._model[lastDuration]?.amount || this.defaultAmount
                        };

                        this.onModelChanged();
                    }
                }

                this._updateRateDurations();
            });
    }

    deleteRate(duration: number) {
        const value = this._model[duration];

        delete this._model[duration];

        this.onModelChanged();
        this._updateRateDurations();

        this.snackBar.showUndo('Rate deleted')
            .afterDismissed()
            .subscribe(result => {
                if (result.dismissedByAction) {
                    this._model[duration] = value;

                    this.onModelChanged();
                    this._updateRateDurations();

                    this.changeDetectorRef.detectChanges();
                }
            });
    }

    private _updateRateDurations() {
        this.ratesDurations = Array.from(
            new Set<number>(Object.keys(this._model).map(duration => parseInt(duration, 10)))
        );
    }

    _trackRateFn(index: number, duration: number) {
        return duration;
    }
}
